import type { CSS } from '@stitches/react';
import type { ForwardedRef } from 'react';
import React from 'react';
import { mergeProps, useField } from 'react-aria';

import type { ToggleInputProps } from '../../components/ToggleInput/ToggleInput';
import type { BaseFieldProps } from './common/types';
import { Alert } from '../../components/Alert/Alert';
import { ToggleInput } from '../../components/ToggleInput/ToggleInput';
import { Tooltip } from '../../components/Tooltip/Tooltip';
import FeatureBadge from '../../formatting/FeatureBadge/FeatureBadge';
import { styled } from '../../stitches.config';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import {
  type AccessLevel,
  isAccessLevelVisible,
  useAccessLevelContext,
} from '../../utilities/useAccessLevelContext';
import filterErrorMessage from '../../utils/filterErrorMessage';
import { FieldAnchorTarget, useFieldAnchor } from './common/FieldAnchor';
import { FieldDivider, fieldDividerDisplay } from './common/FieldDivider';
import { PrimaryFieldDescription, PrimaryFieldLabel } from './common/FieldLabel';
import { useCustomFieldProps } from './createCustomFieldPropsProvider';
import { MaybeFieldContainer } from './FieldContainer';
import { FieldErrorMessages } from './FieldError';
import { RegisterFieldError } from './FieldError/FieldErrorContext';

const PrimaryToggleFieldAlert = styled(Alert, {
  padding: '$8 $12',
});
PrimaryToggleFieldAlert.displayName = 'PrimaryToggleFieldAlert';

const PrimaryToggleFieldContainer = styled('div', FieldAnchorTarget, {
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  padding: '$12 $16',
  [fieldDividerDisplay]: 'none',

  '&:not(:last-child)': {
    paddingBottom: 0,
    [fieldDividerDisplay]: 'flex',
  },
});
PrimaryToggleFieldContainer.displayName = 'PrimaryToggleFieldContainer';

const PrimaryToggleFieldBadges = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '$6',
});
PrimaryToggleFieldBadges.displayName = 'PrimaryToggleFieldBadges';

export interface PrimaryToggleFieldProps extends BaseFieldProps, ToggleInputProps {
  align?: 'center' | 'justify' | 'left' | 'right';
  tooltip?: React.ReactNode;
  /**
   * If true, the toggle will be set to an indeterminate state if the `selected` prop is undefined,
   * and the user can click back to it after changing the value.
   */
  supportsIndeterminate?: boolean;
  containerProps?: { css: CSS };
}

export function PrimaryToggleFieldInner(
  props: PrimaryToggleFieldProps,
  forwardedRef: ForwardedRef<HTMLLabelElement>,
) {
  const { fieldProps: customFieldProps, inputProps: customInputProps } = useCustomFieldProps();
  const anchor = useFieldAnchor(customFieldProps.name);

  const {
    label = customFieldProps.label,
    errorMessage = customFieldProps.errorMessage,
    description,
    supportsIndeterminate = false,
    accessLevel: accessLevelProp,
    internal,
    containerProps,
    tooltip,
    align,
    ...extraInputProps
  } = props;

  const {
    labelProps,
    fieldProps: inputProps,
    descriptionProps,
    errorMessageProps,
  } = useField({
    label,
    description,
    errorMessage,
  });

  const accessLevel = useAccessLevelContext(accessLevelProp as AccessLevel);

  const badge = accessLevel
    ? isAccessLevelVisible(accessLevel) && <FeatureBadge type={accessLevel} size="x-small" />
    : internal && <FeatureBadge type="internal" size="x-small" />;

  const indeterminate =
    supportsIndeterminate &&
    'selected' in customInputProps &&
    customInputProps.selected === undefined;

  const input = (
    <MaybeFieldContainer>
      <PrimaryToggleFieldContainer {...anchor.containerProps} {...containerProps}>
        <AlignStack
          gap={8}
          preset="body"
          end={
            <ToggleInput
              aria-label={`${label}`}
              size="large"
              canResetToIndeterminate={supportsIndeterminate}
              indeterminate={indeterminate}
              {...mergeProps(customInputProps, inputProps, extraInputProps, {
                ref: forwardedRef,
              })}
            />
          }
        >
          <AlignStack
            gap={8}
            preset="body"
            width="fit-content"
            end={
              (badge || anchor.link) && (
                <PrimaryToggleFieldBadges>
                  {badge}
                  {anchor.link}
                </PrimaryToggleFieldBadges>
              )
            }
          >
            <PrimaryFieldLabel {...labelProps}>{label}</PrimaryFieldLabel>
          </AlignStack>
        </AlignStack>
        {description && (
          <PrimaryFieldDescription {...descriptionProps} align={align}>
            {description}
          </PrimaryFieldDescription>
        )}
        <FieldDivider context="primary" />
      </PrimaryToggleFieldContainer>
      {customFieldProps?.name && <RegisterFieldError name={customFieldProps?.name} />}
      {errorMessage && (
        <FieldErrorMessages>
          <PrimaryToggleFieldAlert
            {...errorMessageProps}
            type="inline"
            relation="stacked"
            variant="negative"
            heading={filterErrorMessage(errorMessage)}
          />
        </FieldErrorMessages>
      )}
    </MaybeFieldContainer>
  );

  return tooltip ? (
    <Tooltip contents={tooltip} asChild={false}>
      {input}
    </Tooltip>
  ) : (
    input
  );
}

export const PrimaryToggleField = React.forwardRef(PrimaryToggleFieldInner);
